import {
  createContext,
  Dispatch,
  Fragment,
  memo,
  PropsWithChildren,
  ReactNode,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from 'react';

import { useRouter } from 'react-resource-router';

import { useNavigationData } from 'src/hooks/use-navigation-data';
import { RouteContext } from 'src/router/routes/types';
import { getRouteFeaturesFromAppData } from 'src/router/utils/route-features';
import Navigation from 'src/sections/global/containers/navigation-v2';

import { useAppData } from './data';
import { NavigationWrapper } from './navigation-wrapper';
import { UniversalRoutes } from './universal-routes';

export type NavigationContextType = {
  navigationType?: 'container' | 'product' | 'focused';
  mobileHeading?: ReactNode | string;
  setNavContextValue?: Dispatch<
    SetStateAction<
      Pick<NavigationContextType, 'navigationType' | 'mobileHeading'>
    >
  >;
};

export type NavigationContextValuesType = Pick<
  NavigationContextType,
  'navigationType' | 'mobileHeading'
>;

export const NavigationContext = createContext<NavigationContextType>({});

export const useNavigationContext = (): NavigationContextType =>
  useContext(NavigationContext);

type NavigationProps = PropsWithChildren<{
  isBeingRenderedInsideMobileNav: boolean;
}>;

const NavigationImpl = memo(
  ({ isBeingRenderedInsideMobileNav }: NavigationProps) => {
    const navigationData = useNavigationData();
    const { navigationType } = useNavigationContext();

    return (
      <Navigation
        {...navigationData}
        isBeingRenderedInsideMobileNav={isBeingRenderedInsideMobileNav}
        navigationType={navigationType}
      />
    );
  }
);

export const UniversalNavigation = memo(() => {
  const [routerState] = useRouter();
  const appData = useAppData();
  const { isNavigationRestructureEnabled } =
    getRouteFeaturesFromAppData(appData);
  const [navContextValue, setNavContextValue] =
    useState<NavigationContextValuesType>({});

  const value: NavigationContextType = useMemo(
    () => ({
      navigationType: navContextValue.navigationType,
      mobileHeading: navContextValue.mobileHeading,
      setNavContextValue,
    }),
    [
      navContextValue.navigationType,
      navContextValue.mobileHeading,
      setNavContextValue,
    ]
  );

  if (isNavigationRestructureEnabled) {
    const { route } = routerState as RouteContext;
    const RouteLayout = route?.layout || Fragment;
    return (
      <NavigationContext.Provider value={value}>
        <RouteLayout />
        <NavigationWrapper Component={NavigationImpl} />
      </NavigationContext.Provider>
    );
  } else {
    return <UniversalRoutes />;
  }
});
